import { useId, useState, type CSSProperties, type ReactNode } from 'react';
import { IconDotsVertical } from '@tabler/icons-react';
import { IconButton } from '../../primitives/IconButton.js';
import { Popover } from '../../primitives/Popover.js';
import { cx } from '../../utils/cx.js';
import { WP_ICON_STROKE } from '../../icons/constants.js';
import { getDataTableCellProps } from './dataTableCell.js';
import type { DataTableDensity } from './dataTableCell.js';
import styles from './DataTable.module.css';
import menuStyles from './DataTableMenus.module.css';

export type RowMenuItemProps = {
  children: ReactNode;
  onClick?: () => void;
  danger?: boolean;
  disabled?: boolean;
};

export function RowMenuItem({ children, onClick, danger, disabled }: RowMenuItemProps) {
  return (
    <button
      type="button"
      className={cx(menuStyles.rowMenuItem, danger && menuStyles.rowMenuItemDanger)}
      disabled={disabled}
      onClick={() => {
        onClick?.();
      }}
    >
      {children}
    </button>
  );
}

export function RowMenuDivider() {
  return <div className={menuStyles.rowMenuDivider} role="separator" />;
}

export function RowMenu({
  children,
  className,
  style,
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div className={cx(menuStyles.rowMenu, className)} style={style}>
      {children}
    </div>
  );
}

export function DataTableRowActionsContent({
  leading,
  menu,
  stacked,
  compact,
  stickyEnd,
  columnWidthStyle,
}: {
  leading?: ReactNode;
  menu?: ReactNode;
  stacked?: boolean;
  compact?: boolean;
  stickyEnd?: boolean;
  columnWidthStyle?: CSSProperties;
}) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const hasMenu = Boolean(menu);
  const hasLeading = Boolean(leading);
  if (!hasMenu && !hasLeading) return null;

  const cluster = (
    <div className={cx(styles.actionCluster, compact && styles.actionClusterCompact)}>
      {hasLeading ? <div className={styles.actionLeading}>{leading}</div> : null}
      {hasLeading && hasMenu ? <span className={styles.actionDivider} aria-hidden /> : null}
      {hasMenu ? (
        <Popover
          opened={open}
          onChange={setOpen}
          portal
          align="end"
          panelId={panelId}
          portalEstimatedHeight={180}
          target={
            <IconButton
              type="button"
              variant="soft"
              size="xs"
              aria-label="Actions sur la ligne"
              aria-controls={panelId}
            >
              <IconDotsVertical size={16} stroke={WP_ICON_STROKE} aria-hidden />
            </IconButton>
          }
        >
          <div className={menuStyles.rowMenu} onClick={() => setOpen(false)}>
            {menu}
          </div>
        </Popover>
      ) : null}
    </div>
  );

  if (stacked) {
    return (
      <div
        className={styles.stackedActions}
        data-datatable-rowclick-ignore
        onClick={(e) => e.stopPropagation()}
      >
        {cluster}
      </div>
    );
  }

  const density: DataTableDensity = compact ? 'compact' : 'default';
  const actionCell = getDataTableCellProps({
    element: 'td',
    part: 'actions',
    pin: stickyEnd ? 'end' : 'none',
    density,
  });

  return (
    <td
      {...actionCell}
      data-datatable-rowclick-ignore
      onClick={(e) => e.stopPropagation()}
      style={{ ...columnWidthStyle, ...actionCell.style }}
    >
      {cluster}
    </td>
  );
}
